import {
  alpha,
  Box,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ChartsLabel,
  ChartsLabelMark,
  ChartsTooltipContainer,
  ChartsTooltipPaper,
  LineChart,
  useAxesTooltip,
  useLegend,
} from '@mui/x-charts';
import { CardSection, CardSubSection, useIsMobile } from '@side/pantry';
import { useMemo, useRef } from 'react';
import { ExportToPNGButton } from '../../components/ExportToPNGButton';
import { GlossaryTooltip } from '../../components/GlossaryTooltip';
import { PerformanceFilterValuesFooter } from '../../FilterValuesFooter/FilterValuesFooter';
import {
  formatAbbreviatedPrice,
  shouldHidePeriodComparisons,
  useViewReportAs,
} from '../../helpers';
import { NoResultsOverlay } from '../../NoResultsOverlay/NoResultsOverlay';
import { useReportsRole } from '../../ReportsConfiguration/useReportsRole';
import { usePerformanceReportContext } from '../context/PerformanceReportContext';
import { useGetPerformanceData } from '../hooks/useGetPerformanceData';
import { axisFormatter } from './PerformanceOverTimeChart.helpers';

const MONTH_LABEL_MAP = {
  January: 'Jan',
  February: 'Feb',
  March: 'Mar',
  April: 'Apr',
  May: 'May',
  June: 'Jun',
  July: 'Jul',
  August: 'Aug',
  September: 'Sep',
  October: 'Oct',
  November: 'Nov',
  December: 'Dec',
};

const VOLUME_IMAGE_NAME = 'volume-over-time.png';
const ADJUSTED_GCI_IMAGE_NAME = 'adjusted-gci-over-time.png';
const CHART_HEIGHT = 320;
const CHART_MOBILE_MARGIN = { left: 0, right: 30, bottom: 10 };
const FOOTER_SX = { pt: { xs: 0, md: 2 }, pl: { xs: 3, md: 0 } };

const getXAxisConfig = (theme: Theme, isMobile: boolean) => ({
  id: 'x-axis',
  dataKey: 'month',
  label: 'Close Date',
  scaleType: 'point' as const,
  tickPlacement: 'middle' as const,
  height: isMobile ? 35 : 50,
  tickLabelStyle: {
    fill: theme.palette.grey[900],
    fontWeight: theme.typography.body2.fontWeight,
    fontSize: isMobile ? '8px' : theme.typography.body3.fontSize,
    lineHeight: 1.3,
  },
  labelStyle: {
    fill: theme.palette.text.secondary,
    fontSize: isMobile ? '8px' : theme.typography.body2.fontSize,
  },
  valueFormatter: (
    value: keyof typeof MONTH_LABEL_MAP,
    context: { location: string },
  ) =>
    context.location === 'tick'
      ? (MONTH_LABEL_MAP[value as keyof typeof MONTH_LABEL_MAP] ?? value)
      : value,
});

const getYAxisConfig = (theme: Theme, isMobile: boolean) => ({
  scaleType: 'linear' as const,
  valueFormatter: axisFormatter,
  width: 60,
  tickLabelStyle: {
    fill: theme.palette.grey[900],
    fontWeight: theme.typography.body2.fontWeight,
    fontSize: isMobile ? '8px' : theme.typography.body2.fontSize,
    lineHeight: 1.3,
  },
  position: 'left' as const,
  min: 0,
});

const priorYearLookupLabel = (
  seriesId: PerformanceOverTimeChartSeriesId | string | number | undefined,
) => {
  if (seriesId === PerformanceOverTimeChartSeriesId.priorYearVolume) {
    return 'Prior Year Volume';
  }
  if (seriesId === PerformanceOverTimeChartSeriesId.priorYearAdjustedGci) {
    return 'Prior Year Post-Referral GCI';
  }
  return undefined;
};

const PerformanceOverTimeChartLegend = () => {
  const { items: rawItems } = useLegend();

  // Sort legend so the main data series appears first, then prior year, then goal
  const items = [...rawItems].sort((a, b) => {
    const priority = (id: string | undefined) => {
      if (!id) return 1;
      if (id.startsWith('prior-year')) return 2;
      if (id.includes('goal')) return 3;
      return 0; // main series first
    };
    return priority(a.seriesId as string) - priority(b.seriesId as string);
  });

  return (
    <Stack direction="row" sx={{ width: '100%' }}>
      <Stack
        sx={{
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: { xs: 'flex-start', md: 'center' },
          flexWrap: { xs: 'nowrap', md: 'wrap' },
          pl: { xs: 2, md: 0 },
          width: '100%',
          gap: { xs: 0.75, md: 1 },
        }}
      >
        <Box
          component="ul"
          sx={(t) => ({
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 1.5, md: 2.75 },
            alignItems: 'center',
            listStyleType: 'none',
            p: 0,
            m: 0,
            '& .MuiChartsLabel-root': {
              ...t.typography.body3,
            },
            '& .MuiChartsLegend-mark, & .MuiChartsLabelMark-root.MuiChartsLabelMark-line':
              {
                width: { xs: 16, md: 28 },
              },
            '& [data-series="volume-goal"]': {
              '& .MuiChartsLabelMark-fill': {
                strokeDasharray: '2 3',
              },
            },
          })}
        >
          {items.map((item, index) => (
            <Box
              key={`${item.seriesId}-${item.dataIndex ?? index}`}
              component="li"
              data-series={item.seriesId}
            >
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 1,
                  '& .MuiChartsLabelMark-root.MuiChartsLabelMark-line': {
                    width: { xs: 16, md: 28 },
                  },
                }}
              >
                <ChartsLabelMark color={item.color} type={item.markType} />
                <GlossaryTooltip
                  label={item.label}
                  lookupLabel={priorYearLookupLabel(item.seriesId)}
                  darkColor={item.color}
                  lightColor={alpha(item.color, 0.3)}
                >
                  <ChartsLabel>{item.label}</ChartsLabel>
                </GlossaryTooltip>
              </Stack>
            </Box>
          ))}
        </Box>
      </Stack>
    </Stack>
  );
};

enum PerformanceOverTimeChartLabel {
  volume = 'Volume',
  adjustedGci = 'Post-Referral GCI',
  weightedGci = 'Weighted GCI',
  volumeGoal = 'Volume Goal',
  priorYearVolume = 'Prior Year',
  priorYearWeightedGci = 'Prior Year Weighted GCI',
  priorYearAdjustedGci = 'Prior Year',
}

enum PerformanceOverTimeChartSeriesId {
  volume = 'volume',
  adjustedGci = 'adjusted-gci',
  volumeGoal = 'volume-goal',
  priorYearVolume = 'prior-year-volume',
  priorYearAdjustedGci = 'prior-year-adjusted-gci',
}

const getMonthlyFormattedValue = (
  row: ReturnType<typeof useGetPerformanceData>['results'][number],
  seriesId: PerformanceOverTimeChartSeriesId,
) => {
  switch (seriesId) {
    case PerformanceOverTimeChartSeriesId.volume:
      return formatAbbreviatedPrice(row.monthlySalesVolume);
    case PerformanceOverTimeChartSeriesId.adjustedGci:
      return formatAbbreviatedPrice(row.monthlyAdjustedGrossCommission);
    case PerformanceOverTimeChartSeriesId.priorYearAdjustedGci:
      return formatAbbreviatedPrice(
        row.monthlyPriorYearAdjustedGrossCommission ?? null,
      );
    case PerformanceOverTimeChartSeriesId.priorYearVolume:
      return formatAbbreviatedPrice(row.monthlyPriorYearSalesVolume ?? null);
    case PerformanceOverTimeChartSeriesId.volumeGoal:
      return formatAbbreviatedPrice(row.monthlySalesValueGoal);
    default:
      return undefined;
  }
};

const getCumulativeFormattedValue = (
  row: ReturnType<typeof useGetPerformanceData>['results'][number],
  seriesId: PerformanceOverTimeChartSeriesId,
) => {
  switch (seriesId) {
    case PerformanceOverTimeChartSeriesId.volume:
      return formatAbbreviatedPrice(row.salesVolume);
    case PerformanceOverTimeChartSeriesId.adjustedGci:
      return formatAbbreviatedPrice(row.adjustedGrossCommission);
    case PerformanceOverTimeChartSeriesId.priorYearVolume:
      return formatAbbreviatedPrice(row.priorYearSalesVolume ?? null);
    case PerformanceOverTimeChartSeriesId.priorYearAdjustedGci:
      return formatAbbreviatedPrice(
        row.priorYearAdjustedGrossCommission ?? null,
      );
    case PerformanceOverTimeChartSeriesId.volumeGoal:
      return formatAbbreviatedPrice(row.salesValueGoal);
    default:
      return undefined;
  }
};

const VALUE_CELL_SX: SxProps = {
  minWidth: 72,
  textAlign: 'right',
  color: 'action.active',
  flexShrink: 0,
};

function PerformanceOverTimeChartTooltip() {
  const tooltipData = useAxesTooltip();
  const viewReportAs = useViewReportAs();
  // at this point we know that viewReportAs is not undefined, is either the view report as id from the url or the session user id
  const { results } = useGetPerformanceData({ viewReportAs: viewReportAs! });

  const axisData = tooltipData?.[0];
  const { axisValue, seriesItems = [] } = axisData ?? {};
  const row = results.find((result) => result.month === axisValue);

  const monthName = typeof axisValue === 'string' ? axisValue : '';

  const sortedSeriesItems = useMemo(
    () => [
      ...seriesItems.filter(
        (item) => item.seriesId === PerformanceOverTimeChartSeriesId.volume,
      ),
      ...seriesItems.filter(
        (item) =>
          item.seriesId === PerformanceOverTimeChartSeriesId.priorYearVolume,
      ),
      ...seriesItems.filter(
        (item) => item.seriesId === PerformanceOverTimeChartSeriesId.volumeGoal,
      ),
      ...seriesItems.filter(
        (item) =>
          item.seriesId === PerformanceOverTimeChartSeriesId.adjustedGci,
      ),
      ...seriesItems.filter(
        (item) =>
          item.seriesId ===
          PerformanceOverTimeChartSeriesId.priorYearAdjustedGci,
      ),
    ],
    [seriesItems],
  );

  return (
    <ChartsTooltipContainer trigger="axis">
      {axisData != null ? (
        <ChartsTooltipPaper>
          <Stack sx={{ px: 1, pt: 1, pb: 1, gap: 0.75 }}>
            <Stack
              direction="row"
              sx={{ alignItems: 'center', gap: 1, pb: 0.25 }}
            >
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" sx={VALUE_CELL_SX}>
                {monthName}
              </Typography>
              <Typography variant="caption" sx={VALUE_CELL_SX}>
                Total
              </Typography>
            </Stack>
            {sortedSeriesItems.map((item) => {
              const seriesId =
                item.seriesId as PerformanceOverTimeChartSeriesId;
              const monthlyValue = row
                ? getMonthlyFormattedValue(row, seriesId)
                : undefined;
              const cumulativeValue = row
                ? getCumulativeFormattedValue(row, seriesId)
                : undefined;

              return (
                <Stack
                  key={item.seriesId}
                  direction="row"
                  sx={{ alignItems: 'center', gap: 1 }}
                >
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'center', gap: 1, flex: 1 }}
                  >
                    <ChartsLabelMark
                      color={item.color}
                      type="line"
                      sx={
                        item.seriesId ===
                        PerformanceOverTimeChartSeriesId.volumeGoal
                          ? {
                              '& .MuiChartsLabelMark-fill': {
                                strokeDasharray: '5 3',
                              },
                            }
                          : undefined
                      }
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: 'action.active' }}
                    >
                      {item.formattedLabel ?? ''}
                    </Typography>
                  </Stack>
                  <Typography variant="h6" component="span" sx={VALUE_CELL_SX}>
                    {monthlyValue ?? '-'}
                  </Typography>
                  <Typography variant="h6" component="span" sx={VALUE_CELL_SX}>
                    {cumulativeValue ?? '-'}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </ChartsTooltipPaper>
      ) : null}
    </ChartsTooltipContainer>
  );
}

export const PerformanceOverTimeChart = ({
  viewReportAs,
}: {
  viewReportAs: string;
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile('md');
  const { isAgent } = useReportsRole();
  const { filters } = usePerformanceReportContext();
  const hideGoalsAndPriorYear = shouldHidePeriodComparisons(
    filters?.reportingPeriod,
  );
  const { results, isLoading } = useGetPerformanceData({ viewReportAs });
  const volumeChartRef = useRef<HTMLDivElement>(null);
  const adjustedGciChartRef = useRef<HTMLDivElement>(null);
  const hasVolumeData = results.some(
    (result) =>
      result.salesVolume != null || result.priorYearSalesVolume != null,
  );

  const hasVolumeGoalData = results.some(
    (result) => (result.salesValueGoal ?? 0) > 0,
  );

  const hasGciData = results.some(
    (result) =>
      result.adjustedGrossCommission != null ||
      result.priorYearAdjustedGrossCommission != null,
  );

  const gciTitle = isAgent
    ? 'My Weighted GCI Over Time'
    : 'Post-Referral GCI Over Time';

  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} sx={{ gap: 4 }}>
      <CardSection
        cardTitle="Volume Over Time"
        id="volume-over-time-chart"
        sx={{ flex: { lg: '1 1 0' }, minWidth: { lg: 0 } }}
        action={
          <ExportToPNGButton
            chartElement={volumeChartRef.current}
            imageName={VOLUME_IMAGE_NAME}
          />
        }
      >
        <CardSubSection sx={{ px: { xs: 0, md: 3 } }}>
          <Box ref={volumeChartRef}>
            <NoResultsOverlay
              isActive={!hasVolumeData && !isLoading}
              message="No performance data for selected filters"
            >
              <LineChart
                slotProps={{
                  line: (ownerState) =>
                    ownerState.seriesId === 'volume-goal'
                      ? { strokeDasharray: '5 3' }
                      : {},
                }}
                slots={{
                  legend: PerformanceOverTimeChartLegend,
                  tooltip: PerformanceOverTimeChartTooltip,
                }}
                dataset={results}
                xAxis={[getXAxisConfig(theme, isMobile)]}
                yAxis={[
                  {
                    ...getYAxisConfig(theme, isMobile),
                    id: 'volume-axis',
                  },
                ]}
                series={[
                  ...(!hideGoalsAndPriorYear
                    ? [
                        {
                          type: 'line' as const,
                          id: PerformanceOverTimeChartSeriesId.priorYearVolume,
                          dataKey: 'priorYearSalesVolume',
                          label: PerformanceOverTimeChartLabel.priorYearVolume,
                          color: theme.palette.success[400],
                          yAxisId: 'volume-axis',
                          connectNulls: true,
                          valueFormatter: (val: number | null) =>
                            formatAbbreviatedPrice(val),
                          showMark: false,
                        },
                      ]
                    : []),
                  ...(!hideGoalsAndPriorYear && hasVolumeGoalData
                    ? [
                        {
                          type: 'line' as const,
                          id: PerformanceOverTimeChartSeriesId.volumeGoal,
                          dataKey: 'salesValueGoal',
                          label: PerformanceOverTimeChartLabel.volumeGoal,
                          color: theme.palette.secondary[500],
                          yAxisId: 'volume-axis' as const,
                          connectNulls: true,
                          valueFormatter: (val: number | null) =>
                            formatAbbreviatedPrice(val),
                          showMark: false,
                        },
                      ]
                    : []),
                  {
                    type: 'line' as const,
                    id: PerformanceOverTimeChartSeriesId.volume,
                    dataKey: 'salesVolume',
                    label: PerformanceOverTimeChartLabel.volume,
                    color: theme.palette.success[600],
                    yAxisId: 'volume-axis',
                    connectNulls: true,
                    valueFormatter: (val) => formatAbbreviatedPrice(val),
                  },
                ]}
                height={CHART_HEIGHT}
                margin={isMobile ? CHART_MOBILE_MARGIN : undefined}
              />
              <PerformanceFilterValuesFooter sx={FOOTER_SX} />
            </NoResultsOverlay>
          </Box>
        </CardSubSection>
      </CardSection>
      <CardSection
        cardTitle={gciTitle}
        id="adjusted-gci-over-time-chart"
        sx={{ flex: { lg: '1 1 0' }, minWidth: { lg: 0 } }}
        action={
          <ExportToPNGButton
            chartElement={adjustedGciChartRef.current}
            imageName={ADJUSTED_GCI_IMAGE_NAME}
          />
        }
      >
        <CardSubSection sx={{ px: { xs: 0, md: 3 } }}>
          <Box ref={adjustedGciChartRef}>
            <NoResultsOverlay
              isActive={!hasGciData && !isLoading}
              message="No performance data for selected filters"
            >
              <LineChart
                slots={{
                  legend: PerformanceOverTimeChartLegend,
                  tooltip: PerformanceOverTimeChartTooltip,
                }}
                dataset={results}
                xAxis={[getXAxisConfig(theme, isMobile)]}
                yAxis={[
                  {
                    ...getYAxisConfig(theme, isMobile),
                    id: 'adjusted-gci-axis',
                  },
                ]}
                series={[
                  ...(!hideGoalsAndPriorYear
                    ? [
                        {
                          type: 'line' as const,
                          id: PerformanceOverTimeChartSeriesId.priorYearAdjustedGci,
                          dataKey: 'priorYearAdjustedGrossCommission',
                          label: isAgent
                            ? PerformanceOverTimeChartLabel.priorYearWeightedGci
                            : PerformanceOverTimeChartLabel.priorYearAdjustedGci,
                          color: theme.palette.primary[400],
                          yAxisId: 'adjusted-gci-axis',
                          connectNulls: true,
                          valueFormatter: (val: number | null) =>
                            formatAbbreviatedPrice(val),
                          showMark: false,
                        },
                      ]
                    : []),
                  {
                    type: 'line' as const,
                    id: PerformanceOverTimeChartSeriesId.adjustedGci,
                    dataKey: 'adjustedGrossCommission',
                    label: isAgent
                      ? PerformanceOverTimeChartLabel.weightedGci
                      : PerformanceOverTimeChartLabel.adjustedGci,
                    color: theme.palette.primary[600],
                    yAxisId: 'adjusted-gci-axis',
                    connectNulls: true,
                    valueFormatter: (val) => formatAbbreviatedPrice(val),
                  },
                ]}
                height={CHART_HEIGHT}
                margin={isMobile ? CHART_MOBILE_MARGIN : undefined}
              />
              <PerformanceFilterValuesFooter sx={FOOTER_SX} />
            </NoResultsOverlay>
          </Box>
        </CardSubSection>
      </CardSection>
    </Stack>
  );
};
